// Copyright ©️ 2026 eVolve MEP, LLC

import { useEvolveGet } from '@evolvemep/api-ts';
import { Box, Button, Card, Title, Flex, Group, Stack, Text, LoadingOverlay } from '@mantine/core';

import { BasePageHeader } from 'components/Mantine/BasePageHeader';
import { useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';
import { isNotNil } from 'helpers/isNotNil';
import { ActiveProductCard } from 'modules/Admin/Licenses/LicensesMantine/ActiveProductCard';
import { InactiveProductCard } from 'modules/Admin/Licenses/LicensesMantine/InactiveProductCard';
import { SSOCard } from 'modules/Admin/Licenses/LicensesMantine/SSOCard';

export const LicensesPage = () => {
  const navigate = useEvolveNavigate('Licenses', true);
  const { data: licenseData, loading: licenseDataLoading } = useEvolveGet('admin/company/licenses', { lazy: false });

  const noPurchasedProducts = isNotNil(licenseData) && licenseData.purchasedProducts.length === 0;

  return (
    <>
      <BasePageHeader
        title="Licenses"
        topRightComponent={
          <>
            <Button
              variant="light"
              component="a"
              href="https://evolvemep.com/renew-licenses"
              target="_blank"
              rel="noreferrer"
            >
              Renew licenses
            </Button>
            <Button
              variant="light"
              component="a"
              href="https://evolvemep.com/license-request"
              target="_blank"
              rel="noreferrer"
            >
              Request additional licenses
            </Button>
          </>
        }
      />
      <Group align="stretch" justify="center" style={{ overflow: 'hidden auto' }}>
        {!noPurchasedProducts && (
          <Card flex={1} bg="gray.0" radius="sm" maw={800}>
            <Flex justify="space-between" py="sm">
              <Box>
                <Title order={4}>Purchased products</Title>
                <Text size="sm" c="dimmed">
                  Products currently enabled for your company.
                </Text>
              </Box>
              <Button variant="light" onClick={() => navigate('/company-members')}>
                Assign licenses
              </Button>
            </Flex>
            <Stack>
              <LoadingOverlay visible={licenseDataLoading} />
              {licenseData?.purchasedProducts.map((productPool) => (
                <ActiveProductCard key={productPool.productPoolId} productPool={productPool} />
              ))}
              {licenseData?.hasSSO === true && <SSOCard active />}
            </Stack>
          </Card>
        )}
        <Card flex={1} bg="gray.0" radius="sm" maw={800}>
          <Box py="sm">
            <Title order={4}>Available products</Title>
            <Text size="sm" c="dimmed">
              Check out more EVOLVE products and services for purchase.
            </Text>
          </Box>
          <Stack>
            <LoadingOverlay visible={licenseDataLoading} />
            {licenseData?.availableProducts.map((product) => (
              <InactiveProductCard key={product.productId} product={product} />
            ))}
            {licenseData?.hasSSO === false && <SSOCard />}
          </Stack>
        </Card>
      </Group>
    </>
  );
};
